import { Card, Stack } from '@mantine/core';
import { Text } from '@repo/foundations';
import { DataTable, type DataTableColumn } from '@repo/mantine-datatable';
import { useMemo } from 'react';
import {
	HEADER_HEIGHT,
	ROW_HEIGHT,
	useTableStyles as useStyles,
} from '../../../components/TableV2/TableV2.styles';
import { toTitleCase } from '../../../utils/charts/utils';

interface BIMetricResultsRenderProps<T = Record<string, unknown>> {
	results: T[];
}

export function BiMetricResultsRender<T = Record<string, unknown>>({
	results,
}: BIMetricResultsRenderProps<T>) {
	const tableMaxHeight =
		HEADER_HEIGHT + Math.min(15, results.length) * ROW_HEIGHT + 10;

	const { classes: tableClasses } = useStyles({
		hideCheckbox: false,
		noBorder: true,
		disableSelectedDisplay: true,
		withHover: true,
	});

	const columns = useMemo((): DataTableColumn<T>[] => {
		if (!results || results.length === 0) {
			return [];
		}

		const allKeys = new Set<keyof T>();
		results.forEach((result) => {
			Object.keys(result as Record<string, unknown>).forEach((key) =>
				allKeys.add(key as keyof T)
			);
		});

		return Array.from(allKeys).map((key) => {
			// Check if column is numeric (all non-empty values are numeric)
			const values = results.map((result) => result[key]);
			const nonEmptyValues = values.filter(
				(value) => value !== null && value !== undefined && value !== ''
			);

			const isNumeric =
				nonEmptyValues.length > 0 &&
				nonEmptyValues.every((value) => {
					if (typeof value === 'number') {
						return true;
					}

					if (typeof value === 'string') {
						const trimmed = value.trim();
						return (
							trimmed !== '' &&
							!isNaN(Number(trimmed)) &&
							isFinite(Number(trimmed))
						);
					}

					return false;
				});

			return {
				accessor: String(key),
				title: toTitleCase(String(key)),
				textAlignment: isNumeric ? 'right' : 'left',
			} as DataTableColumn<T>;
		});
	}, [results]);

	if (results.length === 0) {
		return (
			<Stack h="100%" align="center" justify="center">
				<Text size="sm" color="text/secondary/default">
					No results available
				</Text>
			</Stack>
		);
	}

	return (
		<Card p={0} shadow="sm">
			<DataTable
				classNames={tableClasses}
				records={results}
				columns={columns}
				maxHeight={tableMaxHeight}
				height={tableMaxHeight}
				withColumnBorders
				textSelectionDisabled={false}
				noRecordsText="No results available"
				sx={{
					'tbody td': {
						fontSize: '12px',
					},
				}}
			/>
		</Card>
	);
}
